export interface LazyObjectUrls {
    get: (id: string) => Promise<string>;
    dispose: () => void;
}

const DISPOSED_MESSAGE = 'This file is no longer open.';

// One object URL per id, made on first request; dispose revokes them all, and a load that
// lands after dispose never gets a URL. A failed load is forgotten so the next request retries.
export function createLazyObjectUrls(load: (id: string) => Promise<Blob>): LazyObjectUrls {
    const requests = new Map<string, Promise<string>>();
    const created = new Set<string>();
    let disposed = false;

    const request = async (id: string): Promise<string> => {
        const blob = await load(id);
        if (disposed) throw new Error(DISPOSED_MESSAGE);
        const url = URL.createObjectURL(blob);
        created.add(url);
        return url;
    };

    const get = (id: string): Promise<string> => {
        if (disposed) return Promise.reject(new Error(DISPOSED_MESSAGE));
        const known = requests.get(id);
        if (known) return known;
        const pending = request(id).catch((error: unknown) => {
            requests.delete(id);
            throw error;
        });
        requests.set(id, pending);
        return pending;
    };

    const dispose = (): void => {
        disposed = true;
        created.forEach((url) => URL.revokeObjectURL(url));
        created.clear();
        requests.clear();
    };

    return { get, dispose };
}
